import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Colour theme: an explicit choice, or `system` to follow the OS.
 *
 * The stored value and the applied value are deliberately different things. `system` has to
 * survive a reload as `system` — collapsing it to whatever the OS said at the time would mean
 * a user who picked "follow my system" stops following it the moment their OS switches.
 */
export type ThemeChoice = 'dark' | 'light' | 'system';
type Resolved = 'dark' | 'light';

/** Shared with the pre-paint script in index.html. Changing it here changes it there. */
const STORAGE_KEY = 'ira.theme';

const LIGHT_QUERY = '(prefers-color-scheme: light)';

function readStored(): ThemeChoice {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'dark' || raw === 'light' || raw === 'system') return raw;
  } catch {
    // Private browsing denies localStorage. Fall through to the default.
  }
  return 'dark';
}

function resolve(choice: ThemeChoice): Resolved {
  if (choice !== 'system') return choice;
  return window.matchMedia(LIGHT_QUERY).matches ? 'light' : 'dark';
}

function apply(resolved: Resolved): void {
  document.documentElement.setAttribute('data-theme', resolved);
  // Keeps the mobile browser chrome from staying black behind a light page.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', resolved === 'light' ? '#ffffff' : '#08080a');
}

interface ThemeContextValue {
  choice: ThemeChoice;
  resolved: Resolved;
  setChoice: (next: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readStored);
  const [resolved, setResolved] = useState<Resolved>(() => resolve(readStored()));

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Preference is lost on reload but the session still honours it.
    }
  }, []);

  useEffect(() => {
    const next = resolve(choice);
    setResolved(next);
    apply(next);
  }, [choice]);

  // Only while following the system, and only for as long as that is the choice: an OS theme
  // change must repaint immediately rather than at the next reload.
  useEffect(() => {
    if (choice !== 'system') return;
    const media = window.matchMedia(LIGHT_QUERY);
    const onChange = () => {
      const next: Resolved = media.matches ? 'light' : 'dark';
      setResolved(next);
      apply(next);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [choice]);

  // Another tab changed the preference. Without this the two tabs disagree until reload.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      setChoiceState(readStored());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ choice, resolved, setChoice }),
    [choice, resolved, setChoice],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside <ThemeProvider>');
  return context;
}
